import Button from '@alltrails/shared/denali/components/Button';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Close from '@alltrails/shared/icons/Close';
import PortalModal from 'components/shared/PortalModal/PortalModal';
import useIsMobile from '@alltrails/shared/hooks/useIsMobile';
import { FormattedMessage, FormattedNumber, useIntl } from '@alltrails/shared/react-intl';
import Typography from '@alltrails/shared/denali/components/Typography';
import StarRating from '@alltrails/shared/denali/components/StarRating';
import InstallPromptType from '@alltrails/analytics/enums/InstallPromptType';
import InstallPromptLocation from '@alltrails/analytics/enums/InstallPromptLocation';
import InstallPromptSource from '@alltrails/analytics/enums/InstallPromptSource';
import { useEffect, useState } from 'react';
import logInstallPromptViewed from '@alltrails/analytics/events/logInstallPromptViewed';
import logInstallPromptDismissed from '@alltrails/analytics/events/logInstallPromptDismissed';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import { Modal } from '@alltrails/modules/Modal';
import useFeatures from 'hooks/useFeatures';
import useExperiment from 'hooks/useExperiment';
import ChevronSm from '@alltrails/shared/icons/ChevronSm';
import logContinueInBrowserClicked from '@alltrails/analytics/events/logContinueInBrowserClicked';
import classNames from 'classnames';
import useExposureEvent from 'hooks/useExposureEvent';
import * as styles from './styles/styles.module.scss';
import appIconSrc from '../../../../assets/images/icons/app_icon.png';
import useDeeplink from '../hooks/useDeeplink';
import { deepLinkRedirect, isAndroid } from '../utils';
import SmartBannerDetection from '../SmartBannerDetection/SmartBannerDetection';
import '@alltrails/modules/Modal/index.css';
import getBrowserData from '../utils/getBrowserData';

type BranchBannerProps = {
  promptLocation: InstallPromptLocation;
  pageId?: number;
  pageType?: 'area' | 'trail';
  isOpen?: boolean;
  onRequestClose?: () => void;
  displayTrigger?: CarouselDisplayTrigger;
};

// Design link: https://www.figma.com/file/eQkMBkn2YnhYkVahhaXrZG/In-House-Branch?type=design&node-id=728-29259&mode=design&t=PL0roJqI0OHiLtzf-0

const BranchBanner = ({ pageId, pageType, promptLocation, isOpen: isOpenAttempt = false, displayTrigger, onRequestClose }: BranchBannerProps) => {
  const isAppsFlyerEnabled = useFeatures()?.includes('appsflyer_migration');
  const isMobile = useIsMobile();
  const intl = useIntl();
  const [smartBannerDetected, setSmartBannerDetected] = useState(false);
  const isOpen = isOpenAttempt && !smartBannerDetected && isMobile;
  const deeplink = useDeeplink({ pageType, pageId, isOpen, promptType: 'banner' });
  const installDrawerExperiment = useExperiment('web-growth-rm-mweb-install-drawer', false);
  const isTreatmentBActive = installDrawerExperiment?.value === 'treatment_b';
  const isTreatmentCActive = installDrawerExperiment?.value === 'treatment_c';
  const isTreatmentDActive = installDrawerExperiment?.value === 'treatment_d';
  const [isMounted, setIsMounted] = useState(false);
  const sendExposure = useExposureEvent('web-growth-rm-mweb-install-drawer');
  const ariaLabelElementId = 'install-prompt-label';

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (
      isOpen &&
      (installDrawerExperiment?.value === 'control' ||
        installDrawerExperiment?.value === 'treatment_b' ||
        installDrawerExperiment?.value === 'treatment_c' ||
        installDrawerExperiment?.value === 'treatment_d')
    ) {
      sendExposure();
    }
  }, [isOpen, installDrawerExperiment, sendExposure]);

  useEffect(() => {
    if (isOpen) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: InstallPromptType.Banner,
        install_prompt_source: isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native,
        signup_modal_trigger: displayTrigger
      });
    }
  }, [displayTrigger, isOpen, promptLocation, isAppsFlyerEnabled]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: InstallPromptType.Banner,
      install_prompt_source: isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native
    });
    onRequestClose();
  };

  const { sources, buttonText } = getBrowserData(intl);

  if (isTreatmentBActive) {
    return (
      <Modal isOpen={isOpen} onCloseRequest={(): null => null} shouldHideHeader ariaLabelElementId={ariaLabelElementId}>
        <div className={styles.variantContainer}>
          <button
            type="button"
            className={styles.optionContainer}
            onClick={() => {
              deepLinkRedirect(
                deeplink,
                InstallPromptType.Banner,
                promptLocation,
                isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native
              );
            }}
          >
            <div className={styles.optionInfo}>
              <img src={appIconSrc} alt="" className={styles.atLogo} />
              <Typography variant="heading200" id={ariaLabelElementId}>
                <FormattedMessage defaultMessage="Continue in app" />
              </Typography>
            </div>
            <ChevronSm orientation="right" />
          </button>
          <div className={styles.divider} />
          <button
            type="button"
            className={styles.optionContainer}
            onClick={() => {
              logContinueInBrowserClicked({
                install_prompt_location: promptLocation,
                install_prompt_type: InstallPromptType.Banner,
                install_prompt_source: isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native
              });
              handleCloseClick();
            }}
          >
            <div className={styles.optionInfo}>
              <div className={styles.browserImageContainer}>
                {isMounted && (
                  <picture>
                    <source srcSet={sources.avif} type="image/avif" />
                    <source srcSet={sources.webp} type="image/webp" />
                    <img alt="" src={sources.png} className={styles.browserLogo} />
                  </picture>
                )}
              </div>
              <Typography variant="heading200">{buttonText}</Typography>
            </div>
            <ChevronSm orientation="right" />
          </button>
        </div>
      </Modal>
    );
  }

  if (isTreatmentCActive || isTreatmentDActive) {
    return (
      <Modal isOpen={isOpen} onCloseRequest={(): null => null} shouldHideHeader>
        <div className={styles.variantContainer}>
          <Typography variant="heading300">
            <FormattedMessage defaultMessage="AllTrails is better in the app" />
          </Typography>
          <div className={styles.divider} />
          <div className={classNames(styles.optionContainer, styles.noPointer)}>
            <div className={styles.optionInfo}>
              <img src={appIconSrc} alt="" className={styles.atLogo} />
              <div className={styles.nameAndRatingContainer}>
                <Typography variant="heading200">AllTrails</Typography>
                <div className={styles.ratingContainer}>
                  <StarRating rating={5} variant="complex" />
                  <Typography variant="text100" component="span" className={styles.ratingNumber}>
                    (<FormattedNumber value={isAndroid() ? 4.8 : 4.9} />)
                  </Typography>
                </div>
              </div>
            </div>
            <Button
              allowWrap
              text={intl.formatMessage({ defaultMessage: 'Get app' })}
              onClick={() =>
                deepLinkRedirect(
                  deeplink,
                  InstallPromptType.Banner,
                  promptLocation,
                  isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native
                )
              }
              className={styles.button}
              testId="banner-button"
              variant="accent"
              size="md"
            />
          </div>
          <div className={styles.divider} />
          <button
            type="button"
            className={styles.optionContainer}
            onClick={() => {
              logContinueInBrowserClicked({
                install_prompt_location: promptLocation,
                install_prompt_type: InstallPromptType.Banner,
                install_prompt_source: isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native
              });
              handleCloseClick();
            }}
          >
            <div className={styles.optionInfo}>
              <div className={styles.browserImageContainer}>
                {isMounted && (
                  <picture>
                    <source srcSet={sources.avif} type="image/avif" />
                    <source srcSet={sources.webp} type="image/webp" />
                    <img alt="" src={sources.png} className={styles.browserLogo} />
                  </picture>
                )}
              </div>
              <Typography variant="heading200">{buttonText}</Typography>
            </div>
            <ChevronSm orientation="right" />
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <PortalModal isOpen={isOpen}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          <IconButton
            className={styles.close}
            icon={{ Component: Close }}
            onClick={handleCloseClick}
            size="sm"
            title={intl.formatMessage({ defaultMessage: 'Close' })}
            testId="close-button"
            variant="flat"
          />
          <img src={appIconSrc} alt="" />
          <div className={styles.nameAndRatingContainer}>
            <Typography variant="heading200">AllTrails</Typography>
            <div className={styles.ratingContainer}>
              <StarRating rating={5} variant="complex" />
              <Typography variant="text100" component="span">
                (<FormattedNumber value={isAndroid() ? 4.8 : 4.9} />)
              </Typography>
            </div>
          </div>
        </div>
        <Button
          allowWrap
          text={intl.formatMessage({ defaultMessage: 'Get app' })}
          onClick={() =>
            deepLinkRedirect(
              deeplink,
              InstallPromptType.Banner,
              promptLocation,
              isAppsFlyerEnabled ? InstallPromptSource.NativeWithAppsflyerLink : InstallPromptSource.Native
            )
          }
          className={styles.button}
          testId="banner-button"
          variant="primary"
          size="md"
        />
        <SmartBannerDetection
          onSmartBannerDetected={() => {
            if (!smartBannerDetected) {
              setSmartBannerDetected(true);
            }
          }}
        />
      </div>
    </PortalModal>
  );
};

export default BranchBanner;
